import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { replaceWithRegion } from 'actions/NavigationActions'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions, GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import type { SearchFormRef } from 'components/Search'
import SearchResultsMap from 'components/Search/SearchResultsMap/SearchResultsMap'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_VILLA } from 'constants/offer'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalHomesAndVillasSearch } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHomesAndVillasSearch'
import { useGlobalHotelsSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import useOfferList from 'hooks/Offers/useOfferList'
import useQueryParams from 'hooks/useQueryParams'
import useStableObjectReference from 'hooks/useStableObjectReference'
import {
  decodeOfferIds,
  decodeSearchParams,
  encodeOfferIds,
  encodeSearchParams,
  getSearchTypeFromFilters,
} from 'lib/search/searchUtils'
import { shouldHideAnytimePrices } from 'lib/search/hideAnytimePrices'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HotelSearchHeader from '../../HotelSearchPage/HotelSearchHeader' // TODO Move these into a common AccommodationSearchResults folder
import { HOMES_AND_VILLAS_FILTERS, HOMES_AND_VILLAS_OFFER_TYPES } from 'constants/offerList'
import { addRecentSearch } from 'actions/SearchActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { replace } from 'connected-react-router'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import config from 'constants/config'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import { useIntl } from 'react-intl'
const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
`

const FloatingBanner = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
  margin-left: ${rem(12)};
`

interface Props {
  filters: App.OfferListFilters
  windowSearch: string
  isLoggedIn: boolean
  regionCode: string
}

function HomesAndVillasSearchMapPage(props: Props) {
  const locationLabels = useLocationSearchLabels()
  const intl = useIntl()
  const {
    filters,
    windowSearch,
  } = props
  const params = parseSearchString(windowSearch)
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const [previousDestination, setPreviousDestination] = useState<string | undefined>()
  const [zeroResultsBanner, setZeroResultsBanner] = useState(false)

  const pageSize = config.search.mapView.pinLimit ?? 20

  const listFilters = useMemo<App.OfferListFilters>(() => ({
    ...filters,
    offerTypes: filters.propertyId ? [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK, OFFER_TYPE_VILLA] : HOMES_AND_VILLAS_OFFER_TYPES,
    holidayTypes: filters.propertyId ? [] : ['Homes & Villas'].concat(filters.holidayTypes ?? []),
    holidayTypesScoped: filters.propertyId ? [] : HOMES_AND_VILLAS_FILTERS.holidayTypesScoped,
    isStream: true,
  }), [filters])

  useEffect(() => {
    if (!windowSearch.includes('bounds') && windowSearch.includes('destination')) {
      setPreviousDestination(windowSearch)
    }
  }, [windowSearch])

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds).map(({ id }) => id)
  ), [params.selectedOfferIds])

  const anyOffersSelected = selectedOfferIds.length > 0

  // Handle adding selected offer IDs to URL
  const setSelectedOffersURLParams = useCallback((offers: Array<App.Offer | App.BedbankOffer>) => {
    const newParams = new URLSearchParams(windowSearch)
    if (offers.length > 0) {
      newParams.set('selectedOfferIds', encodeOfferIds(offers))
    } else {
      newParams.delete('selectedOfferIds')
    }
    const newSearchString = newParams.toString()
    if (newSearchString !== windowSearch) {
      const currentHash = window.location.hash
      dispatch(replace({
        pathname: window.location.pathname,
        search: newSearchString,
        hash: currentHash,
        state: window.history.state,
      }))
    }
  }, [dispatch, windowSearch])

  // Handle deselecting offers
  const onDeselectOffers = useCallback(() => {
    setSelectedOffersURLParams([])
  }, [setSelectedOffersURLParams])

  // decodeSearchParams can change depending on the current date, so it's not enough to
  // just use useMemo with windowSearch as the dependency.
  // Better to evaluate on each render and then stabilise it with useStableObjectReference.
  const searchParams = useStableObjectReference(decodeSearchParams(windowSearch))

  const dates = useMemo(() => ({
    checkIn: searchParams.dates?.checkIn,
    checkOut: searchParams.dates?.checkOut,
  }), [searchParams])
  const rooms = searchParams.rooms

  const searchAreaLabel = intl.formatMessage({
    id: 'hWFFa8WKQa2eFTGn5Nnw',
    defaultMessage: 'Search area',
    description: 'Search item label when user searches by map bounds on homes and villas map page',
  })

  const onBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }

    const searchItem: App.SearchItem = {
      searchType: 'bounds',
      value: newBounds.toUrlValue(),
      format: {
        mainText: searchAreaLabel,
      },
    }

    const searchStr = encodeSearchParams({
      urlSearch: windowSearch,
      searchItem,
      dates,
      rooms,
      filters: listFilters,
      flexibleNights: listFilters?.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters?.flexibleMonths,
      userSelectedFlexibleMonths: true,
    }).toString()
    dispatch(replaceWithRegion('/search/homes-and-villas/map', searchStr))
  }, [windowSearch, dates, rooms, listFilters, dispatch, searchAreaLabel])

  const formRef = useRef<SearchFormRef>(null)

  const searchType = getSearchTypeFromFilters(listFilters)

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.openDatePickerMenu()
  }, [])

  const globalHomesAndVillasSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)
  const isMobileScreen = useScreenSizeOnly('mobile')

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (isMobileScreen) {
      toggleSearchVertical('homes-and-villas', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      if (globalHomesAndVillasSearchDispatchRef.current) {
        globalHomesAndVillasSearchDispatchRef.current({
          type: GlobalSearchStateActions.SET_SEARCH_ITEM,
          searchItem: propertySearchItem,
        })
      }

      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  // Handle errors and zero results
  const [modal, setModal] = useState<'error' | 'offline' | 'zeroResults' | null>(null)
  const clearModal = useCallback(() => setModal(null), [])

  const onError = useCallback(() => {
    if (typeof window.navigator.onLine === 'boolean' && !window.navigator.onLine) {
      setModal('offline')
    } else {
      setModal('error')
    }
  }, [])

  const onZeroResults = useCallback(() => {
    if (!anyOffersSelected) {
      setModal('zeroResults')
    }
  }, [anyOffersSelected])

  const onClickModifySearch = useCallback(() => {
    clearModal()
    setTimeout(() => { formRef.current?.focusLocationInput() }, 250)
  }, [clearModal])

  const searchTargetLabel = (params.landmarkName || params.destinationName)

  const queryParams = useQueryParams()

  const utmBp = useAppSelector(state => state.utm.bp)
  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights
  const hideAnytimePrices = shouldHideAnytimePrices(isAnytimeSearch, utmBp)

  const { globalHomesAndVillasSearchDispatch, globalHomesAndVillasSearchState } = useGlobalHomesAndVillasSearch({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    searchTargetLabel,
    hideAnytimePrices,
  }, queryParams)

  useEffect(() => {
    globalHomesAndVillasSearchDispatchRef.current = globalHomesAndVillasSearchDispatch
  }, [globalHomesAndVillasSearchDispatch])

  useEffect(() => {
    globalHomesAndVillasSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePrices,
    })
  }, [hideAnytimePrices, globalHomesAndVillasSearchDispatch])

  const nearbyPropertyListFilters = useMemo((): App.OfferListFilters => ({ ...listFilters, searchNearby: true }), [listFilters])

  const offerList = useOfferList(listFilters, { minRequiredOffers: pageSize })
  const nearbyPropertyOfferList = useOfferList(nearbyPropertyListFilters, { disabled: !listFilters.propertyId })

  const allHotelLists = useMemo(() => [offerList], [offerList])
  const allNearbyHotelLists = useMemo(() => [nearbyPropertyOfferList], [nearbyPropertyOfferList])
  useEffect(() => {
    setZeroResultsBanner(offerList.offerCount === 0 && !offerList.fetching)
  }, [offerList.fetching, offerList.offerCount])

  useGlobalHotelsSearchResultsTracking(
    globalHomesAndVillasSearchState,
    allHotelLists,
    offerList.key,
    !listFilters.propertyId ? allNearbyHotelLists : undefined,
  )

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const listViewLabel = intl.formatMessage({
    id: 'IOpxgChUzxZXY4yso313',
    defaultMessage: 'List view',
    description: 'Label for switch to list view on homes and villas search map page',
  })

  const offerListFiltersProps = useMemo(() => {
    const listViewSearch = previousDestination || windowSearch

    return {
      searchType,
      switchView: {
        label: listViewLabel,
        view: 'list' as const,
        URL: `/search/homes-and-villas${listViewSearch}`,
      },
      listFilters,
    }
  }, [windowSearch, searchType, previousDestination, listFilters, listViewLabel])

  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams
    if (searchItem && (searchType === 'destination' || searchType === 'property')) {
      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }
      dispatch(addRecentSearch(searchItem, dates, rooms))
    }
  }, [searchParams, searchType, dispatch])

  const loadingOffersLabel = intl.formatMessage({
    id: '_GR_EoErgrxO3D_R499q',
    defaultMessage: 'Loading offers',
    description: 'Loading indicator text while offers load on homes and villas search map page',
  })
  const errorModalTitle = intl.formatMessage({
    id: 'wXYRIhGOWKMz6yV-11M3',
    defaultMessage: 'Looks like something’s gone wrong',
    description: 'Error modal title when search fails on homes and villas map page',
  })
  const refreshSearchLabel = intl.formatMessage({
    id: 'YekpZWLXyLmTnwKNV9lt',
    defaultMessage: 'Refresh search',
    description: 'Button to refresh search on error modal on homes and villas map page',
  })
  const modifySearchLabel = intl.formatMessage({
    id: 'x318nAveJ7m4mJbhavFk',
    defaultMessage: 'Modify search',
    description: 'Button to modify search criteria on homes and villas map page',
  })
  const errorModalBody = intl.formatMessage({
    id: 'sTWB1aEsZUZIbQtF6Z5l',
    defaultMessage: 'Refresh or modify your search to see available offers.',
    description: 'Error modal body text on homes and villas map page',
  })
  const offlineModalTitle = intl.formatMessage({
    id: '3CX4GH1FqRBWBOjLFNfE',
    defaultMessage: 'Looks like you’re offline',
    description: 'Offline modal title on homes and villas map page in the search interface',
  })
  const okLabel = intl.formatMessage({
    id: 'ywCGfTRXBFmvkV1s4XWO',
    defaultMessage: 'OK',
    description: 'OK button label on offline modal on homes and villas map page',
  })
  const offlineModalBody = intl.formatMessage({
    id: 'B4O5ThZl751UVcLAXsgu',
    defaultMessage: 'Connect to the internet to search this area.',
    description: 'Offline modal body text on homes and villas map page',
  })
  const zeroResultsModalTitle = intl.formatMessage({
    id: 'zRNAesCZec6xYm3xBvTJ',
    defaultMessage: 'We couldn’t find any offers that match your search criteria',
    description: 'Zero results modal title on homes and villas map page',
  })
  const zeroResultsModalBody = intl.formatMessage({
    id: '95ORf1qC8MOoD84bugsD',
    defaultMessage: 'Please modify your search and try again.',
    description: 'Zero results modal body text on homes and villas map page',
  })

  return <GlobalSearchContextProvider
    state={{ ...globalHomesAndVillasSearchState, onAnyHotelSelectDatesPrompt }}
    dispatch={globalHomesAndVillasSearchDispatch}
  >
    <Root>
      <Pane shadow="bottom-small">
        <HotelSearchHeader
          filters={listFilters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical="homes-and-villas"
          baseSearchPath="/search/homes-and-villas"
          baseSearchMapPath="/search/homes-and-villas/map"
          locationSearchPlaceholder={locationLabels.homesAndVillasPlaceholderLabel}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />
      </Pane>
      <MapWrapper>
        <SearchResultsMap
          filters={listFilters}
          onBoundsChanged={onBoundsChanged}
          onSelectOffers={setSelectedOffersURLParams}
          onDeselectOffers={onDeselectOffers}
          selectedOfferIds={selectedOfferIds}
          onError={onError}
          onZeroResults={onZeroResults}
          offerListOverride={offerList}
          offerList={offerList}
          fullScreenMap
          zeroResultsBanner={zeroResultsBanner}
          showPriceOnPins
        />
        {offerList.fetching && <FloatingBanner>
          <TextButton
            kind="secondary"
            variant="dark"
            nonInteractable={offerList.fetching}
            endIcon={<ColourfulLoadingAnimation size={8} />}
          >
            {loadingOffersLabel}
          </TextButton>
        </FloatingBanner>}
      </MapWrapper>
    </Root>

    <Modal
      isOpen={modal === 'error'}
      onClose={clearModal}
      title={errorModalTitle}
      primaryActionText={refreshSearchLabel}
      onPrimaryActionClick={() => window.location.reload()}
      secondaryActionText={modifySearchLabel}
      onSecondaryActionClick={onClickModifySearch}
    >
      <BodyText variant="large">{errorModalBody}</BodyText>
    </Modal>

    <Modal
      isOpen={modal === 'offline'}
      onClose={clearModal}
      title={offlineModalTitle}
      primaryActionText={okLabel}
      onPrimaryActionClick={clearModal}
    >
      <BodyText variant="large">{offlineModalBody}</BodyText>
    </Modal>

    <Modal
      isOpen={modal === 'zeroResults'}
      onClose={clearModal}
      title={zeroResultsModalTitle}
      primaryActionText={modifySearchLabel}
      onPrimaryActionClick={onClickModifySearch}
    >
      <BodyText variant="large">{zeroResultsModalBody}</BodyText>
    </Modal>
  </GlobalSearchContextProvider>
}

const mapStateToProps = (state: App.State) => {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(HomesAndVillasSearchMapPage)
